import { useState, useEffect, useCallback } from "react";
import { Eye } from "lucide-react";
import { Button, Modal, Select, Table } from "../../../components";
import axios from "axios";
import {
  SUPERADMIN_COMPANIES_URL,
  GET_SUPPORT_TICKETS_URL,
  GET_SUPPORT_TICKET_URL,
  PATCH_SUPPORT_TICKET_STATUS_URL,
} from "../../../features/UrlHelper";
import "./SupportTicketsSection.scss";

// Tabuľka si hľadanie, radenie aj stránkovanie rieši sama (rovnako ako
// v ostatných sekciách), preto načítavame naraz väčší balík najnovších hlásení.
const FETCH_LIMIT = 200;

const STATUS_LABELS = {
  NEW: "Nové",
  IN_PROGRESS: "Rieši sa",
  RESOLVED: "Vyriešené",
  CLOSED: "Uzavreté",
};

const CATEGORY_LABELS = {
  BUG: "Chyba",
  QUESTION: "Otázka",
  IMPROVEMENT: "Zlepšenie",
  OTHER: "Iné",
};

const EMAIL_STATUS_LABELS = {
  PENDING: "Odosiela sa",
  SENT: "Odoslaný",
  FAILED: "Zlyhal",
};

// Rovnaký odznak sa používa v tabuľke aj v detaile, aby stav e-mailu vyzeral
// na oboch miestach rovnako.
const emailStatusBadge = (val) => (
  <span className={`tickets__badge tickets__badge--${String(val).toLowerCase()}`}>
    {EMAIL_STATUS_LABELS[val] || val || "-"}
  </span>
);

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "Všetky stavy" },
  ...Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

const STATUS_OPTIONS = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));

/**
 * @param {boolean} platform  v platformovej sprave (/sprava) -- hlasenia zo
 *                            vsetkych prevadzok, preto navyse filter prevadzky
 */
function SupportTicketsSection({ platform = false }) {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [sectionError, setSectionError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailStatus, setDetailStatus] = useState("NEW");
  const [resolutionNote, setResolutionNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);


  const loadTickets = useCallback(async () => {
    setLoadingTickets(true);
    setSectionError("");
    try {
      const url = GET_SUPPORT_TICKETS_URL({
        status: statusFilter,
        companyId: companyFilter,
        page: 1,
        pageSize: FETCH_LIMIT,
      });
      const response = await axios.get(url);
      setTickets(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      setSectionError(error?.response?.data?.message || "Hlásenia sa nepodarilo načítať.");
    } finally {
      setLoadingTickets(false);
    }
  }, [statusFilter, companyFilter]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Zoznam prevadzok do filtra -- zo servera, nie z nacitanych hlaseni, aby
  // sa dala vybrat aj prevadzka, ktorej hlasenia su mimo nacitanych 200.
  useEffect(() => {
    if (!platform) return;
    axios
      .get(SUPERADMIN_COMPANIES_URL)
      .then(({ data }) => {
        const firmy = Array.isArray(data) ? data : [];
        setCompanyOptions(
          firmy
            .map((f) => ({ value: String(f.id), label: f.slug ? `${f.name} (${f.slug})` : f.name }))
            .sort((a, b) => a.label.localeCompare(b.label, "sk")),
        );
      })
      .catch(() => setCompanyOptions([]));
  }, [platform]);

  const openDetail = async (ticket) => {
    setDetailLoading(true);
    setDetailError("");
    setDetail({ id: ticket.id, ticket_code: ticket.ticket_code });
    try {
      const response = await axios.get(GET_SUPPORT_TICKET_URL(ticket.id));
      setDetail(response.data);
      setDetailStatus(response.data.status);
      setResolutionNote(response.data.resolution_note || "");
    } catch (error) {
      setDetailError(error?.response?.data?.message || "Detail sa nepodarilo načítať.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setDetailError("");
    try {
      await axios.patch(
        PATCH_SUPPORT_TICKET_STATUS_URL(detail.id),
        { status: detailStatus, resolutionNote },
      );
      setDetail(null);
      await loadTickets();
    } catch (error) {
      setDetailError(error?.response?.data?.message || "Stav sa nepodarilo uložiť.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (value) =>
    value ? new Date(value).toLocaleString("sk-SK", { dateStyle: "short", timeStyle: "short" }) : "-";

  const reporterLabel = (row) => {
    if (!row.reporter_name) return "-";
    return row.reporter_role ? `${row.reporter_name} (${row.reporter_role})` : row.reporter_name;
  };

  return (
    <div className="admin__section">
      <div className="admin__sub-header">
        <h2 className="admin__section-title">Hlásenia chýb</h2>
      </div>

      {sectionError && (
        <p className="admin__form-error" role="alert">
          {sectionError}
        </p>
      )}

      {total > tickets.length && (
        <p className="tickets__hint">
          Zobrazených je {tickets.length} najnovších z {total} hlásení. Zúžte výber filtrom.
        </p>
      )}

      <div className="admin__table-wrapper">
        <Table
          columns={[
            { key: "ticket_code", label: "Tiket" },
            { key: "created_at", label: "Vytvorené", render: (val) => formatDate(val) },
            { key: "company_name", label: "Firma", render: (val) => val || "-" },
            {
              key: "reporter_name",
              label: "Hlásiteľ",
              render: (_, row) => reporterLabel(row),
            },
            {
              key: "category",
              label: "Kategória",
              render: (val) => CATEGORY_LABELS[val] || val,
            },
            { key: "subject", label: "Predmet" },
            {
              key: "status",
              label: "Stav",
              render: (val) => (
                <span className={`tickets__badge tickets__badge--${String(val).toLowerCase()}`}>
                  {STATUS_LABELS[val] || val}
                </span>
              ),
            },
            {
              key: "email_status",
              label: "E-mail",
              render: (val) => emailStatusBadge(val),
            },
            {
              key: "actions",
              label: "Akcie",
              sortable: false,
              render: (_, row) => (
                <div className="admin__table-actions">
                  <button
                    className="admin__icon-btn"
                    onClick={() => openDetail(row)}
                    title="Zobraziť detail"
                  >
                    <Eye size={16} />
                  </button>
                </div>
              ),
            },
          ]}
          data={tickets}
          toolbarContent={
            <>
              {platform && (
                <div className="tickets__filter tickets__filter--company">
                  <Select
                    options={[{ value: "", label: "Všetky prevádzky" }, ...companyOptions]}
                    value={companyFilter}
                    onChange={setCompanyFilter}
                  />
                </div>
              )}
              <div className="tickets__filter tickets__filter--status">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={setStatusFilter}
                />
              </div>
            </>
          }
          searchPlaceholder="Hľadať hlásenie..."
          emptyMessage={loadingTickets ? "Načítavam..." : "Žiadne hlásenia"}
        />
      </div>

      {/* Detail tiketu */}
      <Modal
        isOpen={detail !== null}
        onClose={() => setDetail(null)}
        title={detail?.ticket_code || "Detail hlásenia"}
        size="xlarge"
      >
        {detailLoading ? (
          <p>Načítavam...</p>
        ) : (
          detail && (
            <form className="admin__form" onSubmit={handleStatusSubmit}>
              <h3 className="tickets__subject">{detail.subject}</h3>

              <dl className="tickets__meta">
                <dt>Stav</dt>
                <dd>{STATUS_LABELS[detail.status] || detail.status}</dd>
                <dt>Kategória</dt>
                <dd>{CATEGORY_LABELS[detail.category] || detail.category}</dd>
                <dt>Hlásiteľ</dt>
                <dd>
                  {reporterLabel(detail)}
                  {detail.reporter_email ? ` · ${detail.reporter_email}` : ""}
                </dd>
                <dt>Firma</dt>
                <dd>{detail.company_name || "-"}</dd>
                <dt>Obrazovka</dt>
                <dd className="tickets__url">{detail.page_url || "-"}</dd>
                <dt>Vytvorené</dt>
                <dd>{formatDate(detail.created_at)}</dd>
                <dt>Snímka</dt>
                <dd>{detail.has_screenshot ? "Priložená v e-maile" : "Bez snímky"}</dd>
                <dt>E-mail</dt>
                <dd>{emailStatusBadge(detail.email_status)}</dd>
                {detail.resolved_at && (
                  <>
                    <dt>Vyriešené</dt>
                    <dd>
                      {formatDate(detail.resolved_at)}
                      {detail.resolved_by_name ? ` · ${detail.resolved_by_name}` : ""}
                    </dd>
                  </>
                )}
              </dl>

              <div className="admin__form-field">
                <label className="admin__form-label">Popis</label>
                <div className="tickets__description">{detail.description}</div>
              </div>

              <div className="admin__form-field">
                <label className="admin__form-label">Diagnostika</label>
                <pre className="tickets__diagnostics">
                  {JSON.stringify(detail.diagnostics ?? {}, null, 2)}
                </pre>
              </div>

              <div className="admin__form-field">
                <Select
                  label="Stav"
                  options={STATUS_OPTIONS}
                  value={detailStatus}
                  onChange={setDetailStatus}
                />
              </div>

              <div className="admin__form-field">
                <label className="admin__form-label" htmlFor="tickets-note">
                  Poznámka k riešeniu
                </label>
                <textarea
                  id="tickets-note"
                  className="admin__form-input tickets__textarea"
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  rows={3}
                />
              </div>

              {detailError && (
                <p className="admin__form-error" role="alert">
                  {detailError}
                </p>
              )}

              <div className="admin__form-actions">
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => setDetail(null)}
                  disabled={isSubmitting}
                >
                  Zavrieť
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Ukladám..." : "Uložiť stav"}
                </Button>
              </div>
            </form>
          )
        )}
      </Modal>
    </div>
  );
}

export default SupportTicketsSection;
